import type { PropsWithChildren } from 'react';
import { useTranslations } from 'next-intl';
import { useSpring, animated, config, to } from '@react-spring/web';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';

export const RevealAnswer = ({
  children,
  correctAnswer,
  enableAnimation = true,
}: PropsWithChildren<{
  enableAnimation?: boolean;
  correctAnswer: string | undefined;
}>) => {
  const translate = useTranslations('presenting.legacy');

  const [springs] = useSpring(() => ({
    config: { ...config.slow, precision: 0.0001 },
    from: {
      scale: 0,
      opacity: 0,
      textOpacity: 0,
      y: 600,
    },
    to: [
      {
        scale: 1,
        textOpacity: 1,
        opacity: 0,
        y: 600,
        immediate: !enableAnimation,
      },
      {
        scale: 1,
        textOpacity: 1,
        opacity: 1,
        y: 0,
        immediate: !enableAnimation,
      },
    ],
  }));

  const copy = correctAnswer
    ? `${translate('quiz_results.correct_answer_is')} ${correctAnswer}`
    : translate('quiz_results.no_correct_answer');

  return (
    <Box width="100%" height="100%" flex="1 1 auto" alignItems="center">
      <Box
        as={animated.div}
        width="100%"
        height="100%"
        flex="1 1 auto"
        style={{
          opacity: springs.opacity.to((o: number) => o) as any,
        }}
      >
        {children}
      </Box>
      <Box overflow="visible">
        <Box
          as={animated.div}
          alignItems="center"
          style={{
            opacity: springs.textOpacity.to((o: number) => o) as any,
            transform: to(
              [springs.y, springs.scale],
              (y: number, scale: number) =>
                `translateY(-${y}%) scale(${scale})`,
            ) as any,
          }}
        >
          <Text
            fontWeight="semiBold"
            color="text"
            fontSize="250"
            textAlign="center"
          >
            {copy}
          </Text>
        </Box>
      </Box>
    </Box>
  );
};
